<script setup lang="ts">
import { UIIcon, UITag } from '@groover-dev/groover-ui'

import { addHttps } from '~/mixins/global'

import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'
import { useGrowthBook } from '~/composables/useGrowthBook'

import { useMiscResizeStore } from '~/stores/miscResize'

import { Breakpoints } from '~/enums/breakpoints'

import type { Influencer } from '~/types/influencer'
import type { Platforms } from '~/types/mediaLinks'

class LocalMediaLink {
  link: string
  followers: number
  key: Platforms

  constructor(influencer: Influencer, key: Platforms) {
    this.key = key
    this.link = this.addHttps(influencer.media_links[key])
    this.followers = influencer.followers
      ? (influencer?.followers[key] ?? 0)
      : 0
  }

  private addHttps = addHttps
}

type Props = {
  influencer: Influencer
}
const { influencer } = defineProps<Props>()

const { t } = useI18n()
const { curatorIsContest } = useCuratorContestsInfos(influencer)
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const toggle = ref(false)
const isContest = curatorIsContest(influencer)

const hasAtLeastOneLink = computed<boolean>(() => {
  return Object.values(influencer.media_links).some((value) => {
    return (value?.length || 0) > 0
  })
})

const displaySecondaryOpportunities = computed<boolean>(() => {
  return (
    !!influencer.secondary_opportunities.length &&
    !influencer.secondary_opportunities.includes('__none__')
  )
})

const orderedMediaLinks = computed<LocalMediaLink[]>(() => {
  const keys = Object.keys(influencer.media_links) as Platforms[]
  const unsortedMediaLinks = keys.reduce(
    (accumulator: LocalMediaLink[], key) => {
      if (isValidMediaLink(key))
        accumulator.push(new LocalMediaLink(influencer, key))

      return accumulator
    },
    [],
  )
  return unsortedMediaLinks.sort((elemA, elemB) => {
    return elemB.followers - elemA.followers
  })
})

const displayedText = computed<string>(() => {
  const contestInfo = influencer.contest?.info ?? ''
  return isContest ? contestInfo : influencer.description
})

const longDescription = computed<boolean>(() => {
  return (
    displayedText.value.length > 240 ||
    displayedText.value.split('\n').length > 3
  )
})

const toggleValue = computed<string>(() => {
  return (toggle.value ? t('common.seeless') : t('common.seemore')) as string
})

const { isFeatureOnForGroup } = useGrowthBook()
const showNewCuratorExperience = computed(() =>
  isFeatureOnForGroup('curator-mapping', 'v2'),
)

function isValidMediaLink(key: Platforms): boolean {
  const mediaLink: string | undefined = influencer.media_links[key]
  return mediaLink !== undefined && mediaLink.length > 0 && mediaLink !== '__'
}
</script>

<template>
  <section class="tw-relative tw-flex tw-w-full tw-flex-col tw-gap-2xl">
    <h2 class="tw-text-h6">
      {{
        isContest
          ? $t('influencer.profile.slug.description.information')
          : $t('influencer.profile.slug.description.description')
      }}
    </h2>
    <p
      id="description"
      class="preWrap"
      :class="{ 'tw-line-clamp-5 sm:tw-line-clamp-3': !toggle }"
      data-allow-mismatch
    >
      {{ displayedText }}
    </p>
    <button
      v-if="longDescription"
      type="button"
      class="tw-w-fit tw-font-bold"
      :aria-expanded="toggle"
      aria-controls="description"
      @click="toggle = !toggle"
    >
      {{ toggleValue }}
    </button>
    <div
      v-if="showNewCuratorExperience"
      class="tw-grid tw-grid-cols-[1fr_3fr] tw-gap-lg"
    >
      <span class="tw-font-medium">{{
        $t('influencer.profile.slug.description.main')
      }}</span>
      <ul class="tw-flex tw-flex-wrap tw-gap-sm">
        <li
          v-for="opportunity in influencer.primary_opportunities"
          :key="opportunity"
        >
          <UITag
            size="base"
            :text="$t(`opportunity.${opportunity}.name`)"
            :data-test-id="`primary-opprtunity-${opportunity}`"
          />
        </li>
      </ul>
      <template v-if="displaySecondaryOpportunities">
        <span class="tw-font-medium">
          {{ $t('influencer.profile.slug.description.others') }}
        </span>
        <ul class="tw-flex tw-flex-wrap tw-gap-sm">
          <li
            v-for="opportunity in influencer.secondary_opportunities"
            :key="opportunity"
          >
            <UITag
              size="sm"
              :text="$t(`opportunity.${opportunity}.secondary`)"
              :data-test-id="`secondary-opprtunity-${opportunity}`"
            />
          </li>
        </ul>
      </template>
    </div>
    <ul
      class="tw-flex tw-flex-col tw-flex-wrap tw-overflow-hidden 375:tw-flex-row"
    >
      <li>
        <a
          v-if="influencer.link.length"
          class="tw-flex tw-cursor-pointer tw-items-center"
          :href="addHttps(influencer.link)"
          target="_blank"
          rel="noopener"
        >
          <UIIcon name="fa6-solid:link" class="tw-text-icon-discrete2" />
          <span class="tw-ml-2 tw-text-sm tw-text-discrete2">{{
            influencer.link
          }}</span>
          <span
            v-if="SCREEN_WIDTH >= Breakpoints.xs375 && hasAtLeastOneLink"
            class="tw-mx-2 tw-text-base tw-text-default-color"
            >·</span
          >
        </a>
      </li>
      <li v-for="(data, index) in orderedMediaLinks" :key="index">
        <a
          class="tw-flex tw-cursor-pointer tw-items-center"
          :href="data.link"
          target="_blank"
          rel="noopener"
        >
          <UIIcon
            :name="`fa6-brands:${data.key}`"
            class="tw-text-icon-discrete2"
            :size="['soundcloud', 'deezer'].includes(data.key) ? 'base' : 'sm'"
          />
          <span
            v-if="data.followers > 0"
            class="tw-ml-2 tw-text-sm tw-text-discrete2"
          >
            <span>{{ data.followers }}</span>
            <span>{{ $t('common.follower', data.followers) }}</span>
          </span>
          <span
            v-if="
              SCREEN_WIDTH >= Breakpoints.xs375 &&
              index < orderedMediaLinks.length - 1
            "
            class="tw-mx-2 tw-text-base tw-text-default-color"
            >·</span
          >
        </a>
      </li>
    </ul>
  </section>
</template>
